import { PackageStatus } from '../../enums';
import { coerceMoneyValue, Money } from '../common';
import { InvestmentPackage } from './InvestmentPackage';

function numberOr(value: unknown): number | null {
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
}

function coerceBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function resolvePackageStatus(value: unknown): PackageStatus | null {
  if (value === null) {
    return null;
  }

  if (typeof value === 'string' && Object.values(PackageStatus).includes(value as PackageStatus)) {
    return value as PackageStatus;
  }

  return null;
}

export function normalizeInvestmentPackage(data: unknown): InvestmentPackage | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const raw = data as Record<string, unknown>;
  if (typeof raw.uuid !== 'string' || !raw.uuid) {
    return null;
  }

  if (typeof raw.lenderUuid !== 'string' || !raw.lenderUuid) {
    return null;
  }

  if (typeof raw.createdAt !== 'string' || !raw.createdAt) {
    return null;
  }

  const money = coerceMoneyValue(raw.money);
  if (!money) {
    return null;
  }

  const advanceCommission = coerceMoneyValue(raw.advanceCommission);
  if (!advanceCommission) {
    return null;
  }

  const withdrawnToMain = coerceMoneyValue(raw.withdrawnToMain);
  if (!withdrawnToMain) {
    return null;
  }

  const dealsCount = numberOr(raw.dealsCount);
  const remainingDeals = numberOr(raw.remainingDeals);

  if (dealsCount === null || remainingDeals === null) {
    return null;
  }

  return {
    uuid: raw.uuid,
    lenderUuid: raw.lenderUuid,
    status: resolvePackageStatus(raw.status),
    money,
    dealsCount,
    remainingDeals,
    economicClasses: stringArray(raw.economicClasses),
    legalClasses: stringArray(raw.legalClasses),
    minTerm: nullableNumber(raw.minTerm),
    maxTerm: nullableNumber(raw.maxTerm),
    enableIncasso: coerceBoolean(raw.enableIncasso),
    enableSameBorrower: coerceBoolean(raw.enableSameBorrower),
    enableCapitalProtection: coerceBoolean(
      raw.enableCapitalProtection ?? raw.enable_capital_protection
    ),
    createdAt: raw.createdAt,
    accruedBonuses: coerceMoneyValue(raw.accruedBonuses) ?? new Money(0, money.currencyCode),
    advanceCommission: advanceCommission,
    withdrawnToMain: withdrawnToMain,
  };
}
